import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";
import { PERSIST_CACHE_KEY } from "./persistedSession";

// §4/D149 — der gesamte Query-Cache wird in localStorage gespiegelt: ein Reload malt SOFORT die
// persistierte Struktur (Jobs/Ordner/Asset-Meta/Titel/Status), KEIN Lade-Spinner. Freshness
// kommt danach nicht mehr über Voll-Refetch, sondern über den Delta-Sync (state/deltaSync, von
// useLiveStatus getrieben). Storage-frei abgesichert (no-op, falls localStorage fehlt/blockt).

function safeStorage(): Storage | null {
  try {
    if (typeof window !== "undefined" && window.localStorage) return window.localStorage;
  } catch {
    /* localStorage geblockt (Privacy-Modus / sandboxed iframe) → keine Persistenz */
  }
  return null;
}

const persister = createSyncStoragePersister({
  storage: safeStorage(),
  key: PERSIST_CACHE_KEY,
});

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister,
  // Aus localStorage höchstens 24 h alten Cache hydrieren; älteres wird verworfen (der Delta-Sync
  // zieht den Gap ohnehin frisch, presigned URLs wären nach 10 min tot → kein Mehrwert).
  maxAge: 24 * 60 * 60 * 1000,
  // Cache-Bust bei inkompatiblen Cache-Shapes (mit Shape-Änderungen bumpen) — alter Cache wird
  // dann verworfen statt fehlinterpretiert.
  buster: "d149-1",
  dehydrateOptions: {
    // Sensible Queries NICHT persistieren: die API-Key-Liste (Account-Surface, §9.16). Ebenso die
    // Jobs-Vorschau-URLs (D155, "job-previews"): kurzlebige presigned URLs (D56) wären nach einem
    // Reload abgelaufen — sie werden ohnehin beim Mount frisch gezogen. Nur erfolgreiche Queries
    // (kein Error-State einfrieren). Mutationen NIE persistieren — die API-Key-Mint-Response trägt
    // den Klartext (Codex-P1), der nie in localStorage landen darf.
    shouldDehydrateQuery: (q) =>
      q.state.status === "success" && q.queryKey[0] !== "api-keys" && q.queryKey[0] !== "job-previews",
    shouldDehydrateMutation: () => false,
  },
};
